import { Controller } from "@hotwired/stimulus"

const SIZE_TOKENS = {
  sm: "modal-sm",
  lg: "modal-lg",
  xl: "modal-xl"
}

export default class extends Controller {
  connect() {
    this.loaderNode = document.getElementById("global-ajax-loader")

    // Explicit binding locks the memory reference for reliable cleanup
    this.clickHandler = this.handleRemoteClick.bind(this)
    this.closeHandler = this.handleCloseEvent.bind(this)
    document.addEventListener("click", this.clickHandler)
    document.addEventListener("givsum:modal:close", this.closeHandler)

    this.activeModalsStack = []
    this.abortController = null
  }

  disconnect() {
    // This now matches perfectly and cleans up memory flawlessly
    document.removeEventListener("click", this.clickHandler)
    document.removeEventListener("givsum:modal:close", this.closeHandler)
    if (this.abortController) this.abortController.abort()
  }

  // Injected content dispatches `givsum:modal:close` (bubbling) to close its own
  // modal programmatically, e.g. after a successful form submit.
  handleCloseEvent(event) {
    const modalElement = event.target.closest(".modal") || this.activeModalsStack.at(-1)?.element
    if (!modalElement) return

    const bsInstance = bootstrap.Modal.getInstance(modalElement)
    if (bsInstance) bsInstance.hide()
  }

  handleRemoteClick(event) {
    const trigger = event.target.closest(".modal-ajax")
    if (!trigger) return

    event.preventDefault()
    event.stopPropagation()

    const requestUrl = trigger.getAttribute("href")
    if (!requestUrl || requestUrl === "#") return

    // A newer request supersedes an in-flight one instead of being silently dropped
    if (this.abortController) this.abortController.abort()

    document.querySelectorAll('.tooltip, .popover').forEach(el => el.remove())

    const modalTitle = trigger.dataset.modalTitle || "Givsum Action"
    const modalSize = this.resolveSizeClass(trigger.dataset.modalSize)

    this.showSpinner()
    this.fetchAndSpawn(requestUrl, modalTitle, modalSize)
  }

  resolveSizeClass(size) {
    if (!size) return ""
    // Accepts short tokens ("lg"/"sm"/"xl") or a raw Bootstrap class for backwards compatibility
    return SIZE_TOKENS[size] || size
  }

  async fetchAndSpawn(url, title, size) {
    const controller = new AbortController()
    this.abortController = controller

    try {
      const response = await fetch(url, {
        headers: { "X-Requested-With": "XMLHttpRequest", "Accept": "text/html" },
        signal: controller.signal
      })
      if (!response.ok) throw new Error(`HTTP Error: ${response.status}`)

      const responseMarkup = await response.text()
      this.hideSpinner()

      this.createNewModalInstance(responseMarkup, title, size)

    } catch (error) {
      if (error.name === "AbortError") return
      console.error("Intercept error processing pipeline:", error)
      this.hideSpinner()
      this.renderTemporaryErrorToast()
    } finally {
      if (this.abortController === controller) this.abortController = null
    }
  }

  createNewModalInstance(markup, initialTitle, size) {
    const modalId = `modal-${Date.now()}`
    const titleId = `${modalId}-title`

    // Parse the incoming markup response block context
    const parser = new DOMParser()
    const doc = parser.parseFromString(markup, "text/html")

    // Look for backend title override parameter configurations
    const titleOverrideNode = doc.querySelector("[data-modal-title-override]")
    let finalTitle = initialTitle
    if (titleOverrideNode) {
      finalTitle = titleOverrideNode.textContent.trim()
      titleOverrideNode.remove()
    }

    const footerNode = doc.querySelector(".modal-footer")
    let footerHTML = ""
    if (footerNode) {
      footerHTML = footerNode.outerHTML
      footerNode.remove()
    }

    const bodyHTML = doc.body ? doc.body.innerHTML : markup

    const template = `
      <div class="modal fade" id="${modalId}" tabindex="-1" aria-hidden="true" aria-labelledby="${titleId}">
        <div class="modal-dialog modal-dialog-centered ${size}">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title" id="${titleId}">${finalTitle}</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body">
              ${bodyHTML}
            </div>
            ${footerHTML}
          </div>
        </div>
      </div>
    `

    document.body.insertAdjacentHTML("beforeend", template)
    const element = document.getElementById(modalId)

    const isStacked = this.activeModalsStack.length > 0

    // Manage visual depth indexes explicitly if multiple layers stack
    if (isStacked) {
      element.classList.add("stack-level-up")
      element.style.zIndex = 1055 + (this.activeModalsStack.length * 10)
    }

    const bsInstance = new bootstrap.Modal(element, {
      // Clicking outside never dismisses - only the close button / an explicit givsum:modal:close does.
      // Stacked layers stay backdrop-less so they don't double-darken the screen.
      backdrop: isStacked ? false : "static"
    })
    bsInstance.show()

    this.activeModalsStack.push({ element, instance: bsInstance })

    this.executeInjectedScripts(element, doc)

    element.addEventListener("hidden.bs.modal", () => {
      bsInstance.dispose()
      element.remove()
      this.activeModalsStack = this.activeModalsStack.filter(item => item.element !== element)

      // Stacked modals share the bottom modal's backdrop, which confuses Bootstrap's
      // own body bookkeeping — reconcile it against whatever is actually left open.
      if (this.activeModalsStack.length > 0) {
        document.body.classList.add("modal-open")
      } else {
        document.body.classList.remove("modal-open")
        document.body.style.removeProperty("padding-right")
        document.body.style.removeProperty("overflow")
      }
    })
  }

  executeInjectedScripts(modalElement, parsedDoc) {
    const scripts = parsedDoc.querySelectorAll("script")
    
    scripts.forEach(oldScript => {
      const newScript = document.createElement("script")
      
      Array.from(oldScript.attributes).forEach(attr => {
        newScript.setAttribute(attr.name, attr.value)
      })
      
      newScript.textContent = oldScript.textContent
      
      modalElement.appendChild(newScript)
    })
  }

  renderTemporaryErrorToast() {
    const alertId = `err-alert-${Date.now()}`
    const errorAlertHTML = `
      <div id="${alertId}" class="position-fixed top-0 start-50 translate-middle-x mt-4 alert alert-warning alert-dismissible fade show" role="alert" style="z-index: 1100;">
        <div class="alert-icon">
          <i class="fa fa-info-circle"></i>
        </div>
        <div class="alert-body">
          <div class="alert-heading">The content could not be loaded.</div>
        </div>
        <a class="alert-close" type="button" data-bs-dismiss="alert" aria-label="Close">
          <i class="fa fa-times" style="cursor: pointer;"></i>
        </a>
      </div>
    `
    document.body.insertAdjacentHTML("beforeend", errorAlertHTML)

    setTimeout(() => {
      const node = document.getElementById(alertId)
      if (node) node.remove()
    }, 3500)
  }

  showSpinner() {
    if (this.loaderNode) this.loaderNode.classList.remove("d-none")
  }

  hideSpinner() {
    if (this.loaderNode) this.loaderNode.classList.add("d-none")
  }
}